// First Created - 08 Oct 2026
// Author - Atul Pandey
// Purpose - To display assigned Session Reference Content
//           including videos, articles, blogs and
//           reflection prompts for Trinity Users
//
// Updated - 11 Oct 2026
// - Replaced standard YouTube iframe with EtVideoPanel2
// - Introduced two panel playlist and viewing experience
// - Added content search by title and description
// - Added desktop playlist vertical scrolling
// - Added responsive mobile layout
// - Retained cohort controlled content visibility
// - Retained support for articles, blogs and external content


import React, {
    useEffect,
    useMemo,
    useState,
} from "react"


import {
    PlusIcon,
    MinusIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    ArrowTopRightOnSquareIcon,
    PlayIcon,
    ChevronRightIcon,
} from "@heroicons/react/24/outline"


import {
    PlayCircleIcon,
    BookOpenIcon,
    DocumentTextIcon,
} from "@heroicons/react/24/solid"


// Update this import path to the actual location
// of your existing EtVideoPanel2 component.
import EtVideoPanel2 from "@/components/media/EtVideoPanel2"



/*
|--------------------------------------------------------------------------
| 01 - Helpers
|--------------------------------------------------------------------------
*/

const getContentId = content => {

    return String(
        content?._id ||
        content?.id ||
        ""
    )

}


const getContentIcon = contentType => {

    switch (contentType) {

        case "Video":
            return PlayCircleIcon

        case "Article":
            return DocumentTextIcon

        default:
            return BookOpenIcon

    }

}


/*
|--------------------------------------------------------------------------
| 01a - YouTube Content Check
|--------------------------------------------------------------------------
*/

const isYouTubeContent = content => {

    return (

        content?.contentType === "Video" &&

        content?.contentProvider === "YouTube" &&

        /^[a-zA-Z0-9_-]{11}$/.test(
            content?.videoId || ""
        )

    )

}


/*
|--------------------------------------------------------------------------
| 01b - Safe External URL
|--------------------------------------------------------------------------
*/

const getSafeExternalUrl = content => {

    if (
        !content?.contentUrl ||
        isYouTubeContent(content)
    ) {
        return ""
    }

    try {

        const url = new URL(
            content.contentUrl
        )

        if (
            url.protocol === "https:" ||
            url.protocol === "http:"
        ) {

            return url.href

        }

    } catch (error) {

        return ""

    }

    return ""

}


/*
|--------------------------------------------------------------------------
| 01c - Active Reflection Prompts
|--------------------------------------------------------------------------
*/

const getReflectionPrompts = content => {

    return (
        content?.reflectionPrompts || []
    )
        .filter(
            prompt =>
                prompt?.active !== false &&
                prompt?.promptText
        )
        .slice()
        .sort(
            (a, b) =>
                (a.displayOrder ?? 0) -
                (b.displayOrder ?? 0)
        )

}



/*
|--------------------------------------------------------------------------
| 02 - Main Component
|--------------------------------------------------------------------------
*/

const EtUserSessionReferenceContentBoard = ({
    referenceContent = [],
}) => {


    /*
    |--------------------------------------------------------------------------
    | 03 - Section Visibility
    |--------------------------------------------------------------------------
    */

    const [
        showReferenceContent,
        setShowReferenceContent,
    ] = useState(false)


    /*
    |--------------------------------------------------------------------------
    | 04 - Selected Content
    |--------------------------------------------------------------------------
    |
    | Only one content item is selected for viewing.
    |
    | The selected item controls the dedicated video panel.
    |
    |--------------------------------------------------------------------------
    */

    const [
        selectedContentId,
        setSelectedContentId,
    ] = useState(null)


    /*
    |--------------------------------------------------------------------------
    | 05 - Playlist Search
    |--------------------------------------------------------------------------
    */

    const [
        searchText,
        setSearchText,
    ] = useState("")


    /*
    |--------------------------------------------------------------------------
    | 06 - Sort Reference Content
    |--------------------------------------------------------------------------
    |
    | The backend returns only the content assigned
    | to the eligible cohort.
    |
    | Retain active filtering and display order.
    |
    |--------------------------------------------------------------------------
    */

    const sortedReferenceContent = useMemo(
        () => {

            return (
                Array.isArray(referenceContent)
                    ? referenceContent
                    : []
            )
                .filter(
                    content =>
                        content?.active !== false
                )
                .slice()
                .sort(
                    (a, b) => {

                        const orderDifference =
                            (a.displayOrder ?? 0) -
                            (b.displayOrder ?? 0)

                        if (
                            orderDifference !== 0
                        ) {
                            return orderDifference
                        }

                        return (
                            a.title || ""
                        ).localeCompare(
                            b.title || ""
                        )

                    }
                )

        },
        [referenceContent]
    )


    /*
    |--------------------------------------------------------------------------
    | 07 - Reference Content Count
    |--------------------------------------------------------------------------
    */

    const referenceContentCount =
        sortedReferenceContent.length


    /*
    |--------------------------------------------------------------------------
    | 08 - Resolve Selected Content
    |--------------------------------------------------------------------------
    |
    | If nothing is selected, select the first content item.
    |
    | If an assignment changes and the previously selected
    | content is removed, fall back to the first available item.
    |
    |--------------------------------------------------------------------------
    */

    const selectedContent = useMemo(
        () => {

            const existingSelection =
                sortedReferenceContent.find(
                    content =>
                        getContentId(content) ===
                        selectedContentId
                )

            return (
                existingSelection ||
                sortedReferenceContent[0] ||
                null
            )

        },
        [
            sortedReferenceContent,
            selectedContentId,
        ]
    )


    /*
    |--------------------------------------------------------------------------
    | 09 - Synchronize Selected Content ID
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        const resolvedId =
            selectedContent
                ? getContentId(selectedContent)
                : null

        if (
            resolvedId !== selectedContentId
        ) {

            setSelectedContentId(
                resolvedId
            )

        }

    }, [
        selectedContent,
        selectedContentId,
    ])


    /*
    |--------------------------------------------------------------------------
    | 10 - Filter Playlist By Search
    |--------------------------------------------------------------------------
    |
    | Search matches:
    |
    | - Content title
    | - Content description
    |
    | Searching does not change the currently selected video.
    |
    |--------------------------------------------------------------------------
    */

    const filteredReferenceContent = useMemo(
        () => {

            const search =
                searchText
                    .trim()
                    .toLowerCase()

            if (!search) {

                return sortedReferenceContent

            }

            return sortedReferenceContent.filter(
                content => {

                    const searchableText = [

                        content.title,

                        content.description,

                    ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase()

                    return searchableText.includes(
                        search
                    )

                }
            )

        },
        [
            sortedReferenceContent,
            searchText,
        ]
    )


    /*
    |--------------------------------------------------------------------------
    | 11 - Selected Content Details
    |--------------------------------------------------------------------------
    */

    const selectedIsYouTube =
        isYouTubeContent(
            selectedContent
        )


    const selectedExternalUrl =
        getSafeExternalUrl(
            selectedContent
        )


    const selectedReflectionPrompts =
        getReflectionPrompts(
            selectedContent
        )


    /*
    |--------------------------------------------------------------------------
    | 12 - Content Selection
    |--------------------------------------------------------------------------
    */

    const handleSelectContent = content => {

        const contentId =
            getContentId(content)

        if (
            !contentId
        ) {
            return
        }

        setSelectedContentId(
            contentId
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 13 - Clear Search
    |--------------------------------------------------------------------------
    */

    const handleClearSearch = () => {

        setSearchText("")

    }


    /*
    |--------------------------------------------------------------------------
    | 14 - No Content
    |--------------------------------------------------------------------------
    |
    | Do not show an empty section if nothing has
    | been assigned to the user.
    |
    |--------------------------------------------------------------------------
    */

    if (
        referenceContentCount === 0
    ) {

        return null

    }


    /*
    |--------------------------------------------------------------------------
    | 15 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div className="m-1 p-1 border border-white rounded font-poppins min-w-0">


            {/*
            |--------------------------------------------------------------------------
            | 15a - Reference Content Section Header
            |--------------------------------------------------------------------------
            */}

            <div className="m-1 p-2 flex flex-wrap justify-between items-center gap-3">


                <div className="flex justify-start items-center min-w-0">


                    <button
                        type="button"
                        onClick={() =>
                            setShowReferenceContent(
                                previous =>
                                    !previous
                            )
                        }
                        aria-expanded={
                            showReferenceContent
                        }
                        aria-label={
                            showReferenceContent
                                ? "Collapse Reference Content"
                                : "Expand Reference Content"
                        }
                        className="flex justify-center items-center cursor-pointer rounded-full h-8 w-8 hover:bg-white m-1 p-1 shrink-0"
                    >

                        {showReferenceContent ? (

                            <MinusIcon
                                className="h-6 w-6 text-orange-600"
                            />

                        ) : (

                            <PlusIcon
                                className="h-6 w-6 text-orange-600"
                            />

                        )}

                    </button>


                    <div className="flex justify-center items-center rounded-full h-9 w-9 bg-orange-100 m-1 p-2 shrink-0">

                        <PlayCircleIcon
                            className="h-6 w-6 text-orange-600"
                        />

                    </div>


                    <div className="min-w-0 mx-2">

                        <p className="font-bold text-lg text-slate-800">

                            Session Reference Content

                        </p>

                        <p className="text-xs italic text-gray-600">

                            Explore perspectives, ideas and reflections.

                        </p>

                    </div>


                </div>


                {/*
                |--------------------------------------------------------------------------
                | Content Count
                |--------------------------------------------------------------------------
                */}

                <div className="flex items-center gap-2">

                    <span className="rounded-full border border-orange-300 bg-orange-50 text-orange-700 px-3 py-1 text-xs font-semibold">

                        {referenceContentCount}{" "}

                        {referenceContentCount === 1
                            ? "Resource"
                            : "Resources"}

                    </span>

                </div>


            </div>



            {/*
            |--------------------------------------------------------------------------
            | 15b - Reference Content Workspace
            |--------------------------------------------------------------------------
            */}

            {showReferenceContent && (

                <div className="m-1 p-2 bg-gray-100 rounded-xl min-w-0">


                    {/*
                    |--------------------------------------------------------------------------
                    | Responsive Two Panel Layout
                    |--------------------------------------------------------------------------
                    |
                    | Desktop:
                    |   Left - 40% searchable playlist
                    |   Right - 60% custom video panel
                    |
                    | Mobile:
                    |   Player first, playlist below
                    |
                    |--------------------------------------------------------------------------
                    */}

                    <div className="flex flex-col lg:flex-row gap-3 min-w-0">


                        {/*
                        |--------------------------------------------------------------------------
                        | 16 - LEFT PANEL - PLAYLIST
                        |--------------------------------------------------------------------------
                        */}

                        <div className="order-2 lg:order-1 w-full lg:w-[40%] lg:shrink-0 min-w-0">


                            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">


                                {/*
                                |--------------------------------------------------------------------------
                                | Playlist Header
                                |--------------------------------------------------------------------------
                                */}

                                <div className="p-3 border-b border-gray-100">

                                    <div className="flex items-center justify-between gap-2 mb-3">

                                        <div>

                                            <p className="text-sm font-bold text-slate-800">

                                                Reference Playlist

                                            </p>

                                            <p className="text-[11px] text-gray-500 mt-1">

                                                Select a resource to explore.

                                            </p>

                                        </div>


                                        <span className="bg-orange-100 text-orange-700 rounded-full px-3 py-1 text-[10px] font-semibold">

                                            {
                                                filteredReferenceContent.length
                                            }{" "}
                                            of{" "}
                                            {
                                                referenceContentCount
                                            }

                                        </span>

                                    </div>


                                    {/*
                                    |--------------------------------------------------------------------------
                                    | Search
                                    |--------------------------------------------------------------------------
                                    */}

                                    <div className="relative">

                                        <MagnifyingGlassIcon
                                            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
                                        />

                                        <input
                                            type="text"
                                            value={searchText}
                                            onChange={event =>
                                                setSearchText(
                                                    event.target.value
                                                )
                                            }
                                            placeholder="Search title or description..."
                                            aria-label="Search reference content"
                                            className="w-full bg-gray-100 border border-gray-200 rounded-lg pl-9 pr-9 py-2.5 text-xs text-slate-700 outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-200"
                                        />


                                        {searchText && (

                                            <button
                                                type="button"
                                                onClick={
                                                    handleClearSearch
                                                }
                                                aria-label="Clear search"
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-600"
                                            >

                                                <XMarkIcon
                                                    className="h-4 w-4"
                                                />

                                            </button>

                                        )}

                                    </div>

                                </div>



                                {/*
                                |--------------------------------------------------------------------------
                                | 17 - Scrollable Playlist
                                |--------------------------------------------------------------------------
                                |
                                | On desktops and larger screens:
                                |
                                | - Max height approximately 520px
                                | - Independent vertical scrollbar
                                |
                                | On mobile:
                                |
                                | - Natural height and page scrolling
                                |
                                |--------------------------------------------------------------------------
                                */}

                                <div
                                    className="
                                        p-2
                                        space-y-2
                                        lg:max-h-[520px]
                                        lg:overflow-y-auto
                                        overscroll-contain
                                    "
                                >


                                    {/*
                                    |--------------------------------------------------------------------------
                                    | No Search Matches
                                    |--------------------------------------------------------------------------
                                    */}

                                    {filteredReferenceContent.length === 0 && (

                                        <div className="bg-gray-50 rounded-lg p-5 text-center">

                                            <MagnifyingGlassIcon
                                                className="h-6 w-6 text-gray-400 mx-auto mb-2"
                                            />

                                            <p className="text-xs font-semibold text-gray-600">

                                                No matching reference content.

                                            </p>

                                            <p className="text-[11px] text-gray-500 mt-2">

                                                Try a different title or description.

                                            </p>


                                            <button
                                                type="button"
                                                onClick={
                                                    handleClearSearch
                                                }
                                                className="text-xs text-orange-600 font-semibold mt-3 hover:underline"
                                            >

                                                Clear Search

                                            </button>

                                        </div>

                                    )}



                                    {/*
                                    |--------------------------------------------------------------------------
                                    | Playlist Cards
                                    |--------------------------------------------------------------------------
                                    */}

                                    {filteredReferenceContent.map(
                                        (content, contentIndex) => {


                                            const contentId =
                                                getContentId(
                                                    content
                                                )


                                            const isSelected =
                                                selectedContent &&
                                                getContentId(
                                                    selectedContent
                                                ) === contentId


                                            const ContentIcon =
                                                getContentIcon(
                                                    content.contentType
                                                )


                                            const reflectionPrompts =
                                                getReflectionPrompts(
                                                    content
                                                )


                                            const isVideo =
                                                isYouTubeContent(
                                                    content
                                                )


                                            return (

                                                <div
                                                    key={contentId}
                                                    className={`
                                                        rounded-xl
                                                        border
                                                        overflow-hidden
                                                        transition-colors
                                                        ${
                                                            isSelected
                                                                ? "bg-orange-50 border-orange-400 ring-1 ring-orange-200"
                                                                : "bg-gray-50 border-gray-200 hover:bg-orange-50/50 hover:border-orange-200"
                                                        }
                                                    `}
                                                >


                                                    {/*
                                                    |--------------------------------------------------------------------------
                                                    | Card Selection Header
                                                    |--------------------------------------------------------------------------
                                                    */}

                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleSelectContent(
                                                                content
                                                            )
                                                        }
                                                        aria-pressed={
                                                            !!isSelected
                                                        }
                                                        className="w-full text-left p-3"
                                                    >


                                                        <div className="flex items-start gap-2">


                                                            {/*
                                                            |--------------------------------------------------------------------------
                                                            | Content Type Icon
                                                            |--------------------------------------------------------------------------
                                                            */}

                                                            <div className={`
                                                                h-9 w-9
                                                                shrink-0
                                                                rounded-lg
                                                                flex items-center justify-center
                                                                ${
                                                                    isSelected
                                                                        ? "bg-orange-200"
                                                                        : "bg-white"
                                                                }
                                                            `}>

                                                                <ContentIcon
                                                                    className="h-5 w-5 text-orange-600"
                                                                />

                                                            </div>



                                                            <div className="min-w-0 flex-1">


                                                                <div className="flex items-start justify-between gap-2">


                                                                    <p className={`
                                                                        text-xs
                                                                        font-bold
                                                                        leading-relaxed
                                                                        break-words
                                                                        ${
                                                                            isSelected
                                                                                ? "text-orange-800"
                                                                                : "text-slate-800"
                                                                        }
                                                                    `}>

                                                                        {content.title}

                                                                    </p>


                                                                    {isSelected ? (

                                                                        <PlayIcon
                                                                            className="h-4 w-4 text-orange-600 shrink-0 mt-0.5"
                                                                        />

                                                                    ) : (

                                                                        <ChevronRightIcon
                                                                            className="h-4 w-4 text-gray-400 shrink-0 mt-0.5"
                                                                        />

                                                                    )}

                                                                </div>



                                                                {/*
                                                                |--------------------------------------------------------------------------
                                                                | Description
                                                                |--------------------------------------------------------------------------
                                                                */}

                                                                {content.description && (

                                                                    <p className={`
                                                                        text-[11px]
                                                                        text-gray-600
                                                                        leading-relaxed
                                                                        mt-2
                                                                        whitespace-pre-wrap
                                                                        ${
                                                                            isSelected
                                                                                ? ""
                                                                                : "line-clamp-3"
                                                                        }
                                                                    `}>

                                                                        {content.description}

                                                                    </p>

                                                                )}



                                                                {/*
                                                                |--------------------------------------------------------------------------
                                                                | Card Metadata
                                                                |--------------------------------------------------------------------------
                                                                */}

                                                                <div className="flex flex-wrap items-center gap-2 mt-3">


                                                                    <span className="rounded-full bg-white border border-gray-100 px-2 py-1 text-[10px] text-gray-600">

                                                                        {content.contentType}

                                                                    </span>


                                                                    {reflectionPrompts.length > 0 && (

                                                                        <span className="rounded-full bg-white border border-gray-100 px-2 py-1 text-[10px] text-gray-600">

                                                                            {
                                                                                reflectionPrompts.length
                                                                            }{" "}
                                                                            {
                                                                                reflectionPrompts.length === 1
                                                                                    ? "Prompt"
                                                                                    : "Prompts"
                                                                            }

                                                                        </span>

                                                                    )}


                                                                    {isSelected && (

                                                                        <span className="rounded-full bg-orange-600 text-white px-2 py-1 text-[10px] font-semibold">

                                                                            {isVideo
                                                                                ? "Now Viewing"
                                                                                : "Selected"}

                                                                        </span>

                                                                    )}

                                                                </div>


                                                            </div>


                                                        </div>


                                                    </button>



                                                    {/*
                                                    |--------------------------------------------------------------------------
                                                    | Selected Card Reflection Prompts
                                                    |--------------------------------------------------------------------------
                                                    |
                                                    | Show prompts within the selected card.
                                                    |
                                                    | Keep other playlist cards compact.
                                                    |
                                                    |--------------------------------------------------------------------------
                                                    */}

                                                    {isSelected &&
                                                        reflectionPrompts.length > 0 && (

                                                        <div className="border-t border-orange-200 bg-white/80 p-3">


                                                            <p className="text-[11px] font-bold text-orange-700 mb-2">

                                                                Thoughts To Explore

                                                            </p>


                                                            <div className="space-y-2">


                                                                {reflectionPrompts.map(
                                                                    (
                                                                        prompt,
                                                                        promptIndex
                                                                    ) => (

                                                                        <div
                                                                            key={
                                                                                prompt._id ||
                                                                                promptIndex
                                                                            }
                                                                            className="flex items-start gap-2"
                                                                        >


                                                                            <span className="text-[11px] text-orange-600 font-bold shrink-0">

                                                                                {promptIndex + 1}.

                                                                            </span>


                                                                            <p className="text-[11px] text-gray-700 leading-relaxed">

                                                                                {prompt.promptText}

                                                                            </p>


                                                                        </div>

                                                                    )
                                                                )}


                                                            </div>


                                                        </div>

                                                    )}


                                                </div>

                                            )

                                        }
                                    )}


                                </div>



                                {/*
                                |--------------------------------------------------------------------------
                                | Desktop Playlist Footer
                                |--------------------------------------------------------------------------
                                */}

                                <div className="hidden lg:block border-t border-gray-100 bg-gray-50 px-3 py-2">

                                    <p className="text-[10px] text-gray-500 italic">

                                        Scroll to explore more references.

                                    </p>

                                </div>


                            </div>


                        </div>



                        {/*
                        |--------------------------------------------------------------------------
                        | 18 - RIGHT PANEL - CONTENT VIEWER
                        |--------------------------------------------------------------------------
                        */}

                        <div className="order-1 lg:order-2 flex-1 min-w-0">


                            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">


                                {/*
                                |--------------------------------------------------------------------------
                                | Viewer Header
                                |--------------------------------------------------------------------------
                                */}

                                <div className="p-3 border-b border-gray-100">


                                    <div className="flex items-center gap-2 mb-2">


                                        <PlayCircleIcon
                                            className="h-5 w-5 text-orange-600"
                                        />


                                        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">

                                            Content Viewer

                                        </p>


                                    </div>



                                    <p className="text-base font-bold text-slate-800 leading-relaxed">

                                        {selectedContent?.title ||
                                            "Select Reference Content"}

                                    </p>



                                    {selectedContent?.description && (

                                        <p className="text-xs text-gray-600 mt-2 leading-relaxed whitespace-pre-wrap">

                                            {selectedContent.description}

                                        </p>

                                    )}


                                </div>



                                {/*
                                |--------------------------------------------------------------------------
                                | 19 - YouTube Custom Video Panel
                                |--------------------------------------------------------------------------
                                |
                                | The player is mounted only for the selected video.
                                |
                                | Changing the selected item unmounts the
                                | previous player and initializes the next one.
                                |
                                | Collapsing Reference Content also
                                | unmounts the player.
                                |
                                |--------------------------------------------------------------------------
                                */}

                                {selectedIsYouTube && (

                                    <div className="p-3">


                                        <div className="w-full bg-black rounded-xl overflow-hidden">


                                            <EtVideoPanel2

                                                key={
                                                    `${getContentId(selectedContent)}-${selectedContent.videoId}`
                                                }

                                                videoId={
                                                    selectedContent.videoId
                                                }

                                                autoPlay={false}

                                                startMuted={true}

                                                loop={false}

                                                className="
                                                    w-full
                                                    aspect-video
                                                    rounded-xl
                                                    overflow-hidden
                                                    shadow-lg
                                                "

                                            />


                                        </div>


                                    </div>

                                )}



                                {/*
                                |--------------------------------------------------------------------------
                                | 20 - External Content
                                |--------------------------------------------------------------------------
                                |
                                | Articles, blogs and other external resources
                                | are presented as links rather than passed
                                | into the YouTube video player.
                                |
                                |--------------------------------------------------------------------------
                                */}

                                {!selectedIsYouTube &&
                                    selectedContent && (

                                    <div className="p-4">


                                        <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 text-center">


                                            <BookOpenIcon
                                                className="h-12 w-12 text-orange-500 mx-auto mb-3"
                                            />


                                            <p className="text-sm font-bold text-gray-700 mb-2">

                                                {selectedContent.contentType} Resource

                                            </p>


                                            <p className="text-xs text-gray-500 mb-5">

                                                Explore the selected reference
                                                in its original publication.

                                            </p>



                                            {selectedExternalUrl ? (

                                                <a
                                                    href={
                                                        selectedExternalUrl
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded-lg px-4 py-3"
                                                >

                                                    Open Reference Content

                                                    <ArrowTopRightOnSquareIcon
                                                        className="h-4 w-4"
                                                    />

                                                </a>

                                            ) : (

                                                <p className="text-xs text-gray-500">

                                                    No external viewing link is available.

                                                </p>

                                            )}


                                        </div>


                                    </div>

                                )}



                                {/*
                                |--------------------------------------------------------------------------
                                | 21 - Selected Content Reflection Context
                                |--------------------------------------------------------------------------
                                |
                                | Compact reminder on the viewer side.
                                |
                                | Detailed prompts remain inside
                                | the selected playlist card.
                                |
                                |--------------------------------------------------------------------------
                                */}

                                {selectedReflectionPrompts.length > 0 && (

                                    <div className="border-t border-gray-100 bg-orange-50/50 px-4 py-3">


                                        <div className="flex items-center gap-2">


                                            <BookOpenIcon
                                                className="h-4 w-4 text-orange-600"
                                            />


                                            <p className="text-xs font-semibold text-orange-800">

                                                {
                                                    selectedReflectionPrompts.length
                                                }{" "}
                                                {
                                                    selectedReflectionPrompts.length === 1
                                                        ? "Thought to Explore"
                                                        : "Thoughts to Explore"
                                                }

                                            </p>


                                        </div>


                                        <p className="text-[11px] text-gray-600 mt-1">

                                            Use the reflection prompts in the
                                            selected playlist card to explore
                                            your own perspectives.

                                        </p>


                                    </div>

                                )}


                            </div>


                        </div>


                    </div>


                </div>

            )}


        </div>

    )

}


export default EtUserSessionReferenceContentBoard